import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  input,
  output,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ConfirmationService } from '@core/services/helpers/confirmation.service';
import {
  DEMAND_PRESENCE_TEXTS,
  RealtimePresenceTexts,
  describeChangeNotice,
  describePresence,
} from '@services/realtime/realtime-presence-text';
import { RealtimeTopicWatcherService } from '@services/realtime/realtime-topic-watcher.service';
import { SharedModule } from '@shared/shared.module';

import { confirmRealtimeReload } from './confirm-realtime-reload';

/**
 * - `notice`: mostra "Fulano {verbo} às HH:mm" com Recarregar e Dispensar.
 * - `silent-reload`: sem alteração local, pede releitura silenciosa (`silentRefreshRequested`) e
 *   não mostra nada; com alteração local, cai no aviso.
 */
export type RealtimeChangeMode = 'notice' | 'silent-reload';

/**
 * Faixa de presença e aviso de mudança por tempo real (card 3496), para qualquer tela de UM item
 * (demanda de derivativos, deal de parceiro, deal do Pipeline de crédito). Guia em `README.md`
 * desta pasta.
 *
 * Entra no tópico (`topic`), anuncia presença (`viewing`, ou `editing` com `hasUnsavedChanges`),
 * mostra quem mais está ali e avisa quando outra pessoa muda o item. Sem servidor de tempo real
 * não mostra nada.
 */
@Component({
  selector: 'app-realtime-presence-banner',
  templateUrl: './realtime-presence-banner.component.html',
  styleUrls: ['./realtime-presence-banner.component.scss'],
  imports: [SharedModule],
  providers: [RealtimeTopicWatcherService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RealtimePresenceBannerComponent {
  /** Tópico com presença (ex.: `RealtimeTopics.partnerDeal(id)`). Nulo = fora do tempo real. */
  readonly topic = input<string | null | undefined>(null);
  readonly hasUnsavedChanges = input<boolean>(false);
  readonly texts = input<RealtimePresenceTexts>(DEMAND_PRESENCE_TEXTS);
  /** Eventos que contam como "outra pessoa mudou o item" (ex.: `partner-deal.changed`). */
  readonly changeEventTypes = input<readonly string[]>([]);
  readonly changeMode = input<RealtimeChangeMode>('notice');

  /** A pessoa pediu Recarregar (e confirmou perder as alterações locais, se havia). */
  readonly reloadRequested = output<void>();
  /** Modo `silent-reload`: outra pessoa mudou o item e não há alteração local. */
  readonly silentRefreshRequested = output<void>();

  private readonly watcher = inject(RealtimeTopicWatcherService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly destroyRef = inject(DestroyRef);

  readonly presenceText = computed(() => describePresence(this.watcher.people(), this.texts()));
  readonly someoneEditing = computed(() => this.watcher.people().some(person => person.editing));
  readonly changeNotice = this.watcher.changeNotice;
  /** "Fulano atualizou este deal às HH:mm" ou, sem autor, "Este deal foi atualizado às HH:mm". */
  readonly changeNoticeTitle = computed(() => {
    const notice = this.changeNotice();
    return notice ? describeChangeNotice(notice, this.texts()) : null;
  });

  constructor() {
    effect(() => {
      const topic = this.topic();
      const types = this.changeEventTypes();
      untracked(() => this.watcher.watch(topic, types));
    });

    effect(() => {
      const editing = this.hasUnsavedChanges();
      untracked(() => this.watcher.setEditing(editing));
    });

    this.watcher.changes$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.handleChange());
  }

  onReload(): void {
    if (!this.hasUnsavedChanges()) {
      this.requestReload();
      return;
    }

    confirmRealtimeReload(this.confirmationService, this.texts(), this.changeNotice()?.actorName)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(confirmed => {
        if (confirmed) {
          this.requestReload();
        }
      });
  }

  dismissChangeNotice(): void {
    this.watcher.clearChangeNotice();
  }

  private requestReload(): void {
    this.watcher.clearChangeNotice();
    this.reloadRequested.emit();
  }

  private handleChange(): void {
    if (this.changeMode() === 'silent-reload' && !this.hasUnsavedChanges()) {
      this.watcher.clearChangeNotice();
      this.silentRefreshRequested.emit();
    }
  }
}
